import { create } from 'zustand';
import type { UserProfile } from '@/types/api';
import { authApi } from '@/api/endpoints';
import { setAccessToken } from '@/api/client';

interface AuthState {
  user: UserProfile | null;
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous';
  error: string | null;

  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Recovers a session on page load using the httpOnly refresh cookie. */
  restore: () => Promise<void>;
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'idle',
  error: null,

  login: async (username, password) => {
    set({ status: 'loading', error: null });
    try {
      const response = await authApi.login(username, password);
      setAccessToken(response.accessToken);
      set({ user: response.user, status: 'authenticated', error: null });
    } catch (error) {
      set({ status: 'anonymous', user: null });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      setAccessToken(null);
      set({ user: null, status: 'anonymous' });
    }
  },

  restore: async () => {
    set({ status: 'loading' });
    try {
      const response = await authApi.refresh();
      setAccessToken(response.accessToken);
      set({ user: response.user, status: 'authenticated' });
    } catch {
      // no valid cookie: the visitor simply has to log in
      setAccessToken(null);
      set({ user: null, status: 'anonymous' });
    }
  },

  can: (permission) => get().user?.permissions.includes(permission) ?? false,

  hasRole: (role) => get().user?.roles.includes(role as never) ?? false,
}));
